import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

// ═══════════════════════════════════════════════════════════════
// SHARED SCHEMAS
// ═══════════════════════════════════════════════════════════════

/**
 * One shared workspace, and whether THIS organization may write it.
 *
 * The list is the whole shared library — three rows today — not just the
 * granted ones. An admin needs to see the boxes they are not ticking as much as
 * the ones they are, and a UI that rendered only grants could not offer a
 * checkbox to create one.
 */
export const AdminSharedWorkspaceGrantSchema = z.object({
  workspaceId: z.string().uuid(),
  slug: z.string().min(1),
  name: z.string().min(1),
  /** True when a `SharedWorkspaceWriteGrant` row exists for this pair. */
  mayWrite: z.boolean()
});

export type AdminSharedWorkspaceGrant = z.infer<typeof AdminSharedWorkspaceGrantSchema>;

// ═══════════════════════════════════════════════════════════════
// REQUEST / RESPONSE SCHEMAS
// ═══════════════════════════════════════════════════════════════

export const AdminSharedWorkspaceGrantsListResponseSchema = z.object({
  grants: z.array(AdminSharedWorkspaceGrantSchema)
});

/**
 * The DESIRED end state for one workspace, not a verb.
 *
 * `mayWrite: true` grants, `false` revokes, and sending the state you already
 * have is a no-op rather than an error — an admin clicking a checkbox wants the
 * box to end up how they left it, and both underlying ports are idempotent.
 * A `grant`/`revoke` verb pair would make a double-click a failure.
 */
export const AdminSharedWorkspaceGrantSetBodySchema = z.object({
  mayWrite: z.boolean()
});

export const AdminSharedWorkspaceGrantSetResponseSchema = AdminSharedWorkspaceGrantSchema;

// ═══════════════════════════════════════════════════════════════
// PATH VARS SCHEMAS
// ═══════════════════════════════════════════════════════════════

export const AdminSharedWorkspaceGrantsOrgPathVarsSchema = z.object({
  orgId: z.string().min(1)
});

export const AdminSharedWorkspaceGrantPathVarsSchema = z.object({
  orgId: z.string().min(1),
  workspaceId: z.string().uuid()
});

// ═══════════════════════════════════════════════════════════════
// ZADMINSHAREDWORKSPACEGRANTS MANIFEST
// ═══════════════════════════════════════════════════════════════

export const ZAdminSharedWorkspaceGrants = {
  List: {
    method: "GET" as const,
    path: "/api/admin/organizations/:orgId/shared-workspace-grants",
    PathVars: AdminSharedWorkspaceGrantsOrgPathVarsSchema,
    Response: ApiSuccessSchema(AdminSharedWorkspaceGrantsListResponseSchema)
  },

  Set: {
    method: "PUT" as const,
    path: "/api/admin/organizations/:orgId/shared-workspace-grants/:workspaceId",
    PathVars: AdminSharedWorkspaceGrantPathVarsSchema,
    Body: AdminSharedWorkspaceGrantSetBodySchema,
    Response: ApiSuccessSchema(AdminSharedWorkspaceGrantSetResponseSchema)
  }
} as const;
